import { Add, Edit, LockPersonOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { AccountList } from "../../components/account/AccountList.component";
import { UsersList } from "../../components/administration";
import { ClientList } from "../../components/client";
import { LeadList } from "../../components/crm/LeadList.component";
import { EmployeeList } from "../../components/employee";
import { ProfileDetails, ResetPassword, UpdateCompany, UpdateCompanyLogoModal } from "../../components/my-profile";
import { ProjectHeader, ProjectList } from "../../components/project";
import { SubcontractorList } from "../../components/subcontractor";
import { BalanceSummaryCard, CompanyActivityList, CompanyDetails, PlanDetails } from "../../components/superadmin";
import { TaskList } from "../../components/task/TaskList.component";
import { ToolsList } from "../../components/tools/ToolsList.component";
import { ActionButton, CardLayout, EmptyState, PageHeaderTabs } from "../../components/v3";
import { VendorList } from "../../components/vendor";
import { WorkerList } from "../../components/worker";
import { PageLayout } from "../../layouts/PageLayout";
import { AdminService, AnalyticsService, CompanyService, SubscriptionService } from "../../services";
import { useTabNavigate } from "../../hooks/usePersistedTableState";

const MODULE_TO_ITEM_MAP = {
  USER_MODULE: { label: "Users", key: "maxUsers" },
  PROJECT_MODULE: { label: "Projects", key: "maxProjects" },
  CLIENT_MODULE: { label: "Clients", key: "maxClients" },
  EMPLOYEE_MODULE: { label: "Employees", key: "maxEmployees" },
  WORKER_MODULE: { label: "Workers", key: "maxWorkers" },
  VENDOR_MODULE: { label: "Vendors", key: "maxVendors" },
  SUBCONTRACTOR_MODULE: { label: "Subcontractors", key: "maxSubcontractors" },
  ACCOUNT_MODULE: { label: "Accounts", key: "maxAccounts" },
};

export const SingleCompany = () => {
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get("tab");

  const [activeTab, setActiveTab] = useState(currentTab || "overview");
  const [totalCount, setTotalCount] = useState(0);
  const [companyDetails, setCompanyDetails] = useState({});
  const [balanceSummary, setBalanceSummary] = useState(null);
  const [adminDetails, setAdminDetails] = useState({});
  const [planDetails, setPlanDetails] = useState({});
  const [features, setFeatures] = useState([]);
  const [overviewCounts, setOverviewCounts] = useState({
    projects: 0,
    workers: 0,
    vendors: 0,
    subcontractors: 0,
    clients: 0,
    employees: 0,
    users: 0,
    accounts: 0,
  });
  const [overviewLimits, setOverviewLimits] = useState({});
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [openCompanyModal, setOpenCompanyModal] = useState(false);
  const [openCompanyLogoModal, setOpenCompanyLogoModal] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();
  // Tab switches replace the history entry, so Back leaves the page in one step; each tab's table keeps its filters.
  const navigateTab = useTabNavigate();

  const fetchData = useCallback(async () => {
    try {
      const { data: company } = await CompanyService.getCompanyDetails(id);
      const { data: balance } = await AnalyticsService.getCompanyBalanceSummary({ companyId: id });
      if (company?.ownerUser?.id) {
        const { data: admin } = await AdminService.getUserDetails(company?.ownerUser?.id);
        setAdminDetails(admin);
      }
      try {
        const { data: limitsData } = await SubscriptionService.getAllLimits(id);
        const usage = limitsData?.usage || {};
        const limits = limitsData?.limits || {};

        const counts = {
          projects: usage?.PROJECT_MODULE?.maxProjects ?? 0,
          workers: usage?.WORKER_MODULE?.maxWorkers ?? 0,
          vendors: usage?.VENDOR_MODULE?.maxVendors ?? 0,
          subcontractors: usage?.SUBCONTRACTOR_MODULE?.maxSubcontractors ?? 0,
          clients: usage?.CLIENT_MODULE?.maxClients ?? 0,
          employees: usage?.EMPLOYEE_MODULE?.maxEmployees ?? 0,
          users: usage?.USER_MODULE?.maxUsers ?? 0,
          accounts: usage?.ACCOUNT_MODULE?.maxAccounts ?? 0,
        };
        setOverviewCounts(counts);

        const limitMap = {};
        Object.entries(MODULE_TO_ITEM_MAP).forEach(([moduleCode, { label, key }]) => {
          const moduleLimits = limits[moduleCode] || {};
          if (moduleLimits[key] !== undefined) {
            limitMap[label] = moduleLimits[key];
          }
        });
        setOverviewLimits(limitMap);
      } catch {
        setOverviewCounts({
          projects: 0,
          workers: 0,
          vendors: 0,
          subcontractors: 0,
          clients: 0,
          employees: 0,
          users: 0,
          accounts: 0,
        });
        setOverviewLimits({});
      }
      const { data: planData } = await SubscriptionService.getAllPlans({ companyId: id });
      if (planData?.rows?.length > 0 && planData?.rows[0]?.id) {
        const { data: plan } = await SubscriptionService.getPlanById(planData?.rows[0]?.id);
        const { data: allModules } = await SubscriptionService.getAllModules();
        const moduleMap = new Map((allModules || []).map((m: any) => [m.id, m]));
        const mergedFeatures = (plan?.planFeatures || []).map((pf: any) => {
          const mod = moduleMap.get(pf.featureId);
          return { ...pf, feature: { ...pf.feature, children: mod?.children || [] } };
        });
        setFeatures(mergedFeatures);
      }
      setPlanDetails(planData?.rows?.[0]);
      setBalanceSummary(balance);
      setCompanyDetails(company);
    } catch (error) {
      toast.error("Failed to fetch company details");
    }
  }, [id]);

  const overviewItems = useMemo(
    () => [
      { label: "Projects", value: overviewCounts.projects, limit: overviewLimits.Projects },
      { label: "Workers", value: overviewCounts.workers, limit: overviewLimits.Workers },
      { label: "Vendors", value: overviewCounts.vendors, limit: overviewLimits.Vendors },
      { label: "Subcontractors", value: overviewCounts.subcontractors, limit: overviewLimits.Subcontractors },
      { label: "Clients", value: overviewCounts.clients, limit: overviewLimits.Clients },
      { label: "Employees", value: overviewCounts.employees, limit: overviewLimits.Employees },
      { label: "Users", value: overviewCounts.users, limit: overviewLimits.Users },
      { label: "Accounts", value: overviewCounts.accounts, limit: overviewLimits.Accounts },
    ],
    [overviewCounts, overviewLimits],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const locality = useMemo(() => {
    return [
      companyDetails?.primaryAddress?.locality,
      companyDetails?.primaryAddress?.state,
      companyDetails?.primaryAddress?.pinCode,
      companyDetails?.primaryAddress?.country,
    ].filter(Boolean);
  }, [companyDetails]);

  const handleUpdateAdmin = () => {
    navigate(`/companies/${id}/edit-admin/${adminDetails?.id}`);
  };

  const handleOpenResetPassword = () => setOpenPasswordModal(true);
  const handleClosePasswordModal = () => setOpenPasswordModal(false);

  const handleOpenUpdateCompany = () => setOpenCompanyModal(true);
  const handleCloseCompanyModal = () => setOpenCompanyModal(false);

  const handleOpenCompanyLogoModal = () => setOpenCompanyLogoModal(true);
  const handleCloseCompanyLogoModal = () => setOpenCompanyLogoModal(false);

  const handleUpdateAdminLogo = () => toast.info("Updating the admin logo is not available at this time.");
  const handleUpdateCompanyLogo = () => handleOpenCompanyLogoModal();

  const handleUpdateAdminStatus = async () => {
    try {
      await AdminService.updateUser(adminDetails.id, { active: !adminDetails.active });
      toast.success("Admin status updated successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to update admin status");
    }
  };

  const handleUpdateCompanyStatus = async () => {
    try {
      await CompanyService.updateCompany(companyDetails.id, { active: !companyDetails.active });
      toast.success("Company status updated successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to update company status");
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigateTab(`/companies/${id}?tab=${tab}`);
  };

  const hasAdmin = Boolean(adminDetails?.id);
  const hasPlan = Boolean(planDetails?.id);

  const renderActiveTab = () => {
    switch (activeTab) {
      case "overview":
        return (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "3fr 1fr", // 75% left, 25% right
              },
              width: "100%",
              padding: "0px 11px 11px 11px",
              boxSizing: "border-box",
            }}
          >
            {/* left column */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {/* Company Details */}
              <CompanyDetails
                avatarSrc={companyDetails?.logoFile?.url}
                name={companyDetails?.name}
                phone={companyDetails?.primaryPhone}
                email={companyDetails?.primaryEmail}
                gstNumber={companyDetails?.taxDetails?.gstNumber}
                panNumber={companyDetails?.taxDetails?.panNumber}
                location={locality.join(", ")}
                isActive={companyDetails?.active}
                onHandleUpdateCompanyLogo={handleUpdateCompanyLogo}
                onEdit={handleOpenUpdateCompany}
                onActivateToggle={handleUpdateCompanyStatus}
                overviewItems={overviewItems}
                overviewLimits={overviewLimits}
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "1fr 3fr", // 75% left, 25% right
                  },
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                {/* left column */}
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <BalanceSummaryCard balanceSummary={balanceSummary} />
                </Box>
                {/* right column */}
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {/* Admin Details */}
                  {hasAdmin ? (
                    <ProfileDetails
                      defaultExpanded={true}
                      title="Admin Information"
                      avatarSrc={emptyAvatar}
                      avatarTooltip="Update admin logo"
                      updateProfilePhoto={handleUpdateAdminLogo}
                      showStatusToggle
                      isActive={adminDetails?.active}
                      onStatusToggle={handleUpdateAdminStatus}
                      buttons={adminActions}
                      rows={[
                        [
                          { label: "First Name", value: adminDetails?.firstName },
                          { label: "Last Name", value: adminDetails?.lastName },
                          { label: "User Name", value: adminDetails?.username },
                        ],
                        [
                          { label: "Email Address", value: adminDetails?.primaryEmail },
                          { label: "Phone Number", value: adminDetails?.primaryPhone },
                          { label: "Status", value: adminDetails?.active ? "Active" : "Inactive" },
                        ],
                      ]}
                      style={{ margin: "11px" }}
                    />
                  ) : (
                    <CardLayout
                      sx={{
                        margin: "11px",
                        minHeight: 230,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <EmptyState fullHeight title="No Admin Assigned" subtitle="This company doesn’t have an admin yet." />
                      <Box sx={{ mt: 2 }}>
                        <ActionButton
                          size="small"
                          variant="outlined"
                          buttonText="Add Admin"
                          buttonIcon={Add}
                          onButtonClick={() => navigate(`/companies/${id}/add-admin`)}
                          sx={{ width: "100%" }}
                        />
                      </Box>
                    </CardLayout>
                  )}
                </Box>
              </Box>

              <CompanyActivityList companyId={Number(id)} />
            </Box>
            {/* right column */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {hasPlan ? (
                <PlanDetails
                  planDetails={planDetails}
                  modules={features}
                  onClick={() => navigate(`/companies/${id}/edit-plan/${planDetails?.id}`)}
                />
              ) : (
                <CardLayout
                  sx={{
                    margin: "0px 11px 11px 11px",
                    minHeight: 350,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <EmptyState fullHeight title="No Plan Assigned" subtitle="This company is not subscribed to any plan." />
                  <Box sx={{ mt: 2 }}>
                    <ActionButton
                      size="small"
                      variant="outlined"
                      buttonText="Add Plan"
                      buttonIcon={Add}
                      onButtonClick={() => navigate(`/companies/${id}/add-plan`)}
                      sx={{ width: "100%" }}
                    />
                  </Box>
                </CardLayout>
              )}
            </Box>
          </Box>
        );
      case "crm":
        return (
          <>
            <ProjectHeader title={`${totalCount} Leads`} />
            <LeadList companyId={Number(id)} totalCount={totalCount} setTotalCount={setTotalCount} />
          </>
        );
      case "projects":
        return (
          <>
            <ProjectHeader title={`${totalCount} Projects`} />
            <ProjectList showFilterButton companyId={Number(id)} totalCount={totalCount} setTotalCount={setTotalCount} />
          </>
        );
      case "workers":
        return (
          <>
            <ProjectHeader title={`${totalCount} Workers`} />
            <WorkerList companyId={Number(id)} totalCount={totalCount} setTotalCount={setTotalCount} />
          </>
        );
      case "vendors":
        return (
          <>
            <ProjectHeader title={`${totalCount} Vendors`} />
            <VendorList companyId={Number(id)} totalCount={totalCount} setTotalCount={setTotalCount} />
          </>
        );
      case "subcontractors":
        return (
          <>
            <ProjectHeader title={`${totalCount} Subcontractors`} />
            <SubcontractorList companyId={Number(id)} totalCount={totalCount} setTotalCount={setTotalCount} />
          </>
        );
      case "clients":
        return (
          <>
            <ProjectHeader title={`${totalCount} Clients`} />
            <ClientList companyId={Number(id)} totalCount={totalCount} setTotalCount={setTotalCount} />
          </>
        );
      case "employees":
        return (
          <>
            <ProjectHeader title={`${totalCount} Employees`} />
            <EmployeeList companyId={Number(id)} totalCount={totalCount} setTotalCount={setTotalCount} />
          </>
        );
      case "users":
        return (
          <>
            <ProjectHeader title={`${totalCount} Users`} />
            <UsersList companyId={Number(id)} totalCount={totalCount} setTotalCount={setTotalCount} />
          </>
        );
      case "accounts":
        return (
          <>
            <ProjectHeader title={`${totalCount} Accounts`} />
            <AccountList companyId={Number(id)} totalCount={totalCount} setTotalCount={setTotalCount} />
          </>
        );
      case "task":
        return (
          <>
            <ProjectHeader title={`${totalCount} Tasks`} />
            <TaskList companyId={Number(id)} totalCount={totalCount} setTotalCount={setTotalCount} />
          </>
        );
      case "tools":
        return (
          <>
            <ProjectHeader title={`${totalCount} Tools`} />
            <ToolsList companyId={Number(id)} totalCount={totalCount} setTotalCount={setTotalCount} />
          </>
        );
      default:
        return null;
    }
  };

  const adminActions = [
    { buttonText: "Edit", buttonIcon: Edit, onClick: handleUpdateAdmin },
    { buttonText: "Reset Password", buttonIcon: LockPersonOutlined, onClick: handleOpenResetPassword },
  ];

  const allTabs = [
    "overview",
    "crm",
    "projects",
    "workers",
    "vendors",
    "subcontractors",
    "clients",
    "employees",
    "users",
    "accounts",
    "task",
    "tools",
  ];

  const emptyAvatar =
    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0nMScgaGVpZ2h0PScxJyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnLz4=";
  return (
    <>
      <PageLayout>
        <PageHeaderTabs title="Company Details" tabs={allTabs} activeTab={activeTab} onTabChange={handleTabChange} />
        {renderActiveTab()}
      </PageLayout>

      {/* Change Password Modal */}
      <ResetPassword userId={adminDetails?.id} open={openPasswordModal} onClose={handleClosePasswordModal} />

      {/* Update Company Details */}
      <UpdateCompany
        companyId={companyDetails?.id}
        open={openCompanyModal}
        onClose={handleCloseCompanyModal}
        onSuccess={fetchData}
      />

      {/* Update Company Logo */}
      <UpdateCompanyLogoModal
        companyId={companyDetails?.id}
        open={openCompanyLogoModal}
        onClose={handleCloseCompanyLogoModal}
        onSuccess={fetchData}
      />
    </>
  );
};
